import { useState, useRef, useLayoutEffect, useMemo, type CSSProperties, type ReactNode } from 'react';
import { useApp, Card, Icon, Button, type IconKey } from '@/components/ds';
import { Segmented, CodeBlock } from '@/components/code';
import { genAll } from '@/lib/codegen';
import { PAYMENT_CREATE_SPEC } from '@/data';
import { AgentChat } from '@/containers/agents';
import type { Go } from '@/lib/types';

// home.tsx — Developer Home: the public front door (developers.melio.com/payouts)

// ---- Rails visualization (animated SVG) ------------------------------------
const SVGNS = 'http://www.w3.org/2000/svg';

function RailsViz() {
  const svgRef = useRef<SVGSVGElement>(null);
  const baselinesRef = useRef<SVGGElement>(null);
  const routesRef = useRef<SVGGElement>(null);
  const chipsInRef = useRef<SVGGElement>(null);
  const chipsOutRef = useRef<SVGGElement>(null);
  const pulsesRef = useRef<SVGGElement>(null);

  useLayoutEffect(() => {
    const svg = svgRef.current;
    const elBaseline = baselinesRef.current;
    const elRoutes = routesRef.current;
    const elIn = chipsInRef.current;
    const elOut = chipsOutRef.current;
    const elPulses = pulsesRef.current;
    if (!svg || !elBaseline || !elRoutes || !elIn || !elOut || !elPulses) return;

    const inputs = ['Card', 'Bank', 'Wallet'];
    const outputs = ['Bank', 'Check', 'Virtual card', 'Wire', 'Intl'];
    const ys = [70, 140, 210, 280, 350];
    const inYs = [140, 210, 280];
    const IN_X = 20,
      CHIP_W = 160,
      IN_EDGE = IN_X + CHIP_W;
    const OUT_X = 620;
    const CORE_L = 332,
      CORE_R = 468,
      CORE_Y = 210;

    function makeChip(group: SVGGElement, label: string, x: number, y: number): SVGGElement {
      const g = document.createElementNS(SVGNS, 'g') as SVGGElement;
      const r = document.createElementNS(SVGNS, 'rect');
      r.setAttribute('x', String(x));
      r.setAttribute('y', String(y - 27));
      r.setAttribute('width', String(CHIP_W));
      r.setAttribute('height', '54');
      r.setAttribute('rx', '9');
      r.setAttribute('fill', 'rgba(255,255,255,.045)');
      r.setAttribute('stroke', 'rgba(255,255,255,.13)');
      r.setAttribute('stroke-width', '1');
      const t = document.createElementNS(SVGNS, 'text');
      t.setAttribute('x', String(x + CHIP_W / 2));
      t.setAttribute('y', String(y + 5));
      t.setAttribute('text-anchor', 'middle');
      t.setAttribute('font-family', 'JetBrains Mono, monospace');
      t.setAttribute('font-size', '18');
      t.setAttribute('fill', '#E7E3F2');
      t.setAttribute('font-weight', '500');
      t.textContent = label;
      g.appendChild(r);
      g.appendChild(t);
      group.appendChild(g);
      return g;
    }

    function addBaseline(d: string) {
      const p = document.createElementNS(SVGNS, 'path');
      p.setAttribute('d', d);
      p.setAttribute('stroke', 'rgba(255,255,255,.12)');
      p.setAttribute('stroke-width', '1');
      p.setAttribute('fill', 'none');
      elBaseline!.appendChild(p);
    }

    for (let i = 0; i < inputs.length; i++) {
      makeChip(elIn, inputs[i], IN_X, inYs[i]);
      addBaseline(`M${IN_EDGE},${inYs[i]} C 240,${inYs[i]} 298,${CORE_Y} ${CORE_L},${CORE_Y}`);
    }
    for (let j = 0; j < outputs.length; j++) {
      makeChip(elOut, outputs[j], OUT_X, ys[j]);
      addBaseline(`M${CORE_R},${CORE_Y} C 502,${CORE_Y} 542,${ys[j]} ${OUT_X},${ys[j]}`);
    }

    function routeD(i: number, o: number) {
      return `M${IN_EDGE},${inYs[i]}` + ` C 245,${inYs[i]} 300,${CORE_Y} 400,${CORE_Y}` + ` C 500,${CORE_Y} 542,${ys[o]} ${OUT_X},${ys[o]}`;
    }

    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    interface Pulse {
      path: SVGPathElement;
      dot: SVGCircleElement;
      i: number;
      o: number;
      len: number;
      start: number;
      dur: number;
    }

    const pulses: Pulse[] = [];
    for (let k = 0; k < 3; k++) {
      const path = document.createElementNS(SVGNS, 'path') as SVGPathElement;
      path.setAttribute('stroke', '#8a6bff');
      path.setAttribute('stroke-width', '2');
      path.setAttribute('fill', 'none');
      path.style.opacity = '0';
      elRoutes.appendChild(path);
      const dot = document.createElementNS(SVGNS, 'circle') as SVGCircleElement;
      dot.setAttribute('r', '5');
      dot.setAttribute('fill', 'var(--accent-soft, #c9b6ff)');
      dot.setAttribute('opacity', '0');
      elPulses.appendChild(dot);
      pulses.push({ path, dot, i: 0, o: 0, len: 1, start: 0, dur: 2000 });
    }

    function reinit(p: Pulse, now: number, delay: number) {
      p.i = Math.floor(Math.random() * inputs.length);
      p.o = Math.floor(Math.random() * outputs.length);
      p.path.setAttribute('d', routeD(p.i, p.o));
      p.len = p.path.getTotalLength();
      p.dur = 1800 + Math.random() * 1000;
      p.start = now + delay;
    }

    const t0 = performance.now();
    if (reduce) {
      for (const p of pulses) {
        reinit(p, t0, 0);
        const pt = p.path.getPointAtLength(p.len * 0.5);
        p.dot.setAttribute('cx', String(pt.x));
        p.dot.setAttribute('cy', String(pt.y));
        p.dot.setAttribute('opacity', '1');
        p.path.style.opacity = '0.35';
      }
      return;
    }

    for (let m = 0; m < pulses.length; m++) {
      reinit(pulses[m], t0, m * 700);
    }

    let rafId: number;
    function frame(now: number) {
      for (const p of pulses) {
        if (now < p.start) continue;
        const f = (now - p.start) / p.dur;
        if (f >= 1) {
          p.dot.setAttribute('opacity', '0');
          p.path.style.opacity = '0';
          reinit(p, now, 200 + Math.random() * 450);
          continue;
        }
        p.path.style.opacity = String((0.5 * Math.sin(Math.PI * f)).toFixed(3));
        const q = p.path.getPointAtLength(p.len * f);
        p.dot.setAttribute('cx', String(q.x));
        p.dot.setAttribute('cy', String(q.y));
        p.dot.setAttribute('opacity', String(Math.min(1, Math.sin(Math.PI * f) * 1.6).toFixed(3)));
      }
      rafId = requestAnimationFrame(frame);
    }
    rafId = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(rafId);
      elIn!.replaceChildren();
      elOut!.replaceChildren();
      elBaseline!.replaceChildren();
      elRoutes!.replaceChildren();
      elPulses!.replaceChildren();
    };
  }, []);

  return (
    <div
      style={{
        background: 'radial-gradient(130% 130% at 50% 0%, #2a1c52 0%, #1b1430 42%, #140f22 100%)',
        borderRadius: 20,
        padding: '32px 28px 8px',
        textAlign: 'center',
        overflow: 'hidden',
      }}
    >
      <div className="eyebrow" style={{ color: '#c9b6ff', marginBottom: 14, display: 'block' }}>
        One integration
      </div>
      <h2
        style={{
          color: '#fff',
          fontSize: 'clamp(20px, 2.2vw, 28px)',
          lineHeight: 1.15,
          letterSpacing: '-0.02em',
          margin: 0,
        }}
      >
        Money comes in any way.
        <br />
        It goes out any way.
      </h2>
      <p style={{ color: '#B4AECB', fontSize: 14.5, margin: '10px auto 0', maxWidth: 400, lineHeight: '22px' }}>
        Routed through Melio and out however the recipient wants to be paid — scored and cleared in real time, whether a developer or an
        agent makes the call.
      </p>
      <svg
        ref={svgRef}
        viewBox="0 0 800 400"
        style={{ width: '100%', height: 'auto', display: 'block', marginTop: 28 }}
        aria-label="Money enters by any method, routes through Melio, and exits by any method"
      >
        <text
          x="100"
          y="22"
          textAnchor="middle"
          fontFamily="JetBrains Mono, monospace"
          fontSize="14"
          fill="#8C86A0"
          letterSpacing=".18em"
          style={{ textTransform: 'uppercase' }}
        >
          Money in
        </text>
        <text
          x="700"
          y="22"
          textAnchor="middle"
          fontFamily="JetBrains Mono, monospace"
          fontSize="14"
          fill="#8C86A0"
          letterSpacing=".18em"
          style={{ textTransform: 'uppercase' }}
        >
          Money out
        </text>
        <g ref={baselinesRef} />
        <g ref={routesRef} />
        <g ref={chipsInRef} />
        <g ref={chipsOutRef} />
        <g>
          <rect x="328" y="179" width="144" height="62" rx="12" fill="#1f1733" stroke="var(--accent)" strokeWidth="1.5" />
          <text
            x="400"
            y="217"
            textAnchor="middle"
            fontFamily="Poppins, sans-serif"
            fontWeight="700"
            fontSize="17"
            fill="var(--accent-soft, #c9b6ff)"
            letterSpacing=".04em"
          >
            MELIO
          </text>
        </g>
        <g ref={pulsesRef} />
      </svg>
    </div>
  );
}

// ---- Hero demo (AI agent / REST API toggle) ---------------------------------
interface HeroDemoProps {
  langs: Record<string, string>;
}

function HeroDemo({ langs }: HeroDemoProps) {
  const [view, setView] = useState('agent');
  const H = 428;
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <Segmented
          value={view}
          onChange={setView}
          options={[
            { value: 'agent', label: 'AI agent', icon: 'ai-fill' },
            { value: 'api', label: 'REST API', icon: 'document' },
          ]}
        />
        <span style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: '18px' }}>
          {view === 'api' ? 'One POST creates a payment' : 'The same API, run by an agent'}
        </span>
      </div>
      <div>
        {view === 'api' ? (
          <CodeBlock langs={langs} dark maxHeight={H - 43} style={{ height: H }} />
        ) : (
          <AgentChat height={H} summary={false} collapsedTools />
        )}
      </div>
    </div>
  );
}

// ---- Shared components ------------------------------------------------------
interface SectionHeadProps {
  eyebrow?: string;
  title: string;
  sub?: string;
  style?: CSSProperties;
}

function SectionHead({ eyebrow, title, sub }: SectionHeadProps) {
  return (
    <div style={{ marginBottom: 26, maxWidth: 640 }}>
      {eyebrow && (
        <div className="eyebrow" style={{ color: 'var(--accent)', marginBottom: 8 }}>
          {eyebrow}
        </div>
      )}
      <h2 style={{ margin: 0, fontSize: 30, lineHeight: '36px', letterSpacing: '-0.02em' }}>{title}</h2>
      {sub && <p style={{ fontSize: 16, lineHeight: '24px', color: 'var(--text-secondary)', margin: '12px 0 0' }}>{sub}</p>}
    </div>
  );
}

interface FeatureBoxProps {
  icon: IconKey;
  title: string;
  children: ReactNode;
}

function FeatureBox({ icon, title, children }: FeatureBoxProps) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: '#fff',
        border: `1px solid ${hovered ? 'var(--accent)' : 'var(--border-static)'}`,
        borderRadius: 'var(--radius-card)',
        padding: 24,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        height: '100%',
        transition: 'border-color .18s, transform .18s, box-shadow .18s',
        transform: hovered ? 'translateY(-2px)' : 'none',
        boxShadow: hovered ? '0 5px 15px rgba(33,33,36,.1)' : 'none',
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 12,
          background: 'var(--accent-soft)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon name={icon} size={24} color="var(--accent)" />
      </div>
      <div style={{ fontSize: 17, fontWeight: 600 }}>{title}</div>
      <div style={{ fontSize: 14, lineHeight: '21px', color: 'var(--text-secondary)' }}>{children}</div>
    </div>
  );
}

// ---- Main page --------------------------------------------------------------
interface DevHomeProps {
  go: Go;
  style?: CSSProperties;
}

function DevHome({ go }: DevHomeProps) {
  const app = useApp();
  const heroLangs = useMemo(() => genAll(PAYMENT_CREATE_SPEC, app.apiKey), [app.apiKey]);

  return (
    <div style={{ maxWidth: 1180, margin: '0 auto', padding: '0 28px' }}>
      {/* ── Hero ── */}
      <section className="grid-hero" style={{ padding: '64px 0 56px', alignItems: 'center' }}>
        <div>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              padding: '6px 14px',
              borderRadius: 99,
              background: 'var(--accent-soft)',
              marginBottom: 22,
            }}
          >
            <Icon name="ai-fill" size={14} color="var(--accent)" />
            <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--accent-text)' }}>Melio API · built for humans and agents</span>
          </div>
          <h1 className="display-1" style={{ margin: 0, fontSize: 52, lineHeight: '58px', letterSpacing: '-0.025em' }}>
            Move money on rails
            <br />
            that pay you back.
          </h1>
          <p style={{ fontSize: 18, lineHeight: '28px', color: 'var(--text-secondary)', margin: '20px 0 30px', maxWidth: 500 }}>
            The simplest way to move money between businesses — fund payments by card or bank account and deliver via ACH, virtual card,
            paper check, and more, all through one REST API.
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Button size="large" leftIcon="add" onClick={() => go('/onboarding')}>
              Get your API key
            </Button>
            <Button size="large" variant="tertiary" rightIcon="arrow-right" onClick={() => window.location.assign('/docs')}>
              Read the docs
            </Button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginTop: 30, flexWrap: 'wrap' }}>
            {['Revenue on every payment', 'Any way in or out', 'Humans and agents, one API'].map((t) => (
              <span
                key={t}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13.5, color: 'var(--text-secondary)' }}
              >
                <Icon name="checked-circle-fill" size={16} color="var(--success-700)" />
                {t}
              </span>
            ))}
          </div>
        </div>
        <div style={{ minWidth: 0 }}>
          <RailsViz />
        </div>
      </section>

      {/* ── Why build on Melio ── */}
      <section style={{ marginBottom: 56 }}>
        <div style={{ textAlign: 'center', maxWidth: 680, margin: '0 auto 30px' }}>
          <div className="eyebrow" style={{ color: 'var(--accent)', marginBottom: 14 }}>
            Why build on Melio
          </div>
          <h2 style={{ margin: 0, fontSize: 'clamp(26px, 3.2vw, 38px)', lineHeight: 1.1, letterSpacing: '-0.025em' }}>
            The most monetizable way to move money
          </h2>
        </div>
        <Card
          padding={0}
          style={{ overflow: 'hidden', border: '1px solid rgba(33,33,36,0.08)', boxShadow: '0 5px 15px rgba(33,33,36,.05)' }}
        >
          <div className="grid-3" style={{ gap: 0 }}>
            {(
              [
                [
                  'dollar-fill',
                  'Turn payments into revenue',
                  'Accept card funding and convert interchange into a new revenue stream for your platform.',
                ],
                [
                  'list',
                  'Offer the most payment options',
                  'ACH, virtual card, paper check, wire, and international — give vendors every way to get paid.',
                ],
                [
                  'fast',
                  'Leverage highest acceptance rates',
                  "Melio's network and smart routing maximize successful payment delivery across all methods.",
                ],
              ] as [IconKey, string, string][]
            ).map(([ic, title, desc], i) => (
              <div
                key={title}
                style={{
                  padding: '28px 28px',
                  borderLeft: i ? '1px solid var(--border-static)' : 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 14,
                }}
              >
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 12,
                    background: 'var(--accent-soft)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Icon name={ic} size={24} color="var(--accent)" />
                </div>
                <div style={{ fontSize: 15.5, fontWeight: 600 }}>{title}</div>
                <div style={{ fontSize: 13.5, lineHeight: '20px', color: 'var(--text-secondary)' }}>{desc}</div>
              </div>
            ))}
          </div>
        </Card>
      </section>

      {/* ── Build on the API ── */}
      <section style={{ marginBottom: 56 }}>
        <div className="grid-hero" style={{ alignItems: 'center' }}>
          <div style={{ minWidth: 0 }}>
            <HeroDemo langs={heroLangs} />
          </div>
          <div>
            <div className="eyebrow" style={{ color: 'var(--accent)', marginBottom: 14 }}>
              Build on the API
            </div>
            <h2 style={{ margin: 0, fontSize: 'clamp(26px, 3vw, 38px)', lineHeight: 1.1, letterSpacing: '-0.025em' }}>
              Use Melio's rails, any way you want
            </h2>
            <p style={{ fontSize: 16, lineHeight: '24px', color: 'var(--text-secondary)', margin: '16px 0 26px', maxWidth: 460 }}>
              Drive the API from your own code or hand it to an AI agent. From signup to a live payment is four calls, and the API's
              resource order is the integration order.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 15, marginBottom: 28 }}>
              {(
                [
                  ['Onboard a business', 'Create the legal entity and complete KYC.'],
                  ['Choose where money comes from', "Connect the bank account or card you'll pay from."],
                  ['Choose where money lands', 'Bank transfer, wire, virtual card, or a mailed check.'],
                  ['Create a payment', 'One POST. Track it from scheduled to completed.'],
                ] as [string, string][]
              ).map(([title, desc], i) => (
                <div key={title} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: '50%',
                      background: 'var(--accent)',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: 14,
                      flexShrink: 0,
                    }}
                  >
                    {i + 1}
                  </div>
                  <div>
                    <div style={{ fontSize: 15.5, fontWeight: 600 }}>{title}</div>
                    <div style={{ fontSize: 13.5, color: 'var(--text-secondary)', marginTop: 2 }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <Button variant="secondary" rightIcon="arrow-right" onClick={() => window.location.assign('/docs')}>
              See the full quickstart
            </Button>
          </div>
        </div>
      </section>

      {/* ── AI & agents ── */}
      <section style={{ marginBottom: 64 }}>
        <div
          className="grid-agents"
          style={{
            background: 'linear-gradient(120deg, #1c1428, #16171f 70%)',
            borderRadius: 20,
            padding: '40px 44px',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'absolute',
              right: -60,
              top: -60,
              width: 260,
              height: 260,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(120,73,255,.45), transparent 70%)',
            }}
          />
          <div style={{ position: 'relative' }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 7,
                padding: '4px 11px',
                borderRadius: 99,
                background: 'rgba(183,158,255,.16)',
                color: '#cbb6ff',
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '.04em',
                textTransform: 'uppercase',
                marginBottom: 16,
              }}
            >
              <Icon name="ai-fill" size={14} color="#cbb6ff" />
              For AI agents
            </div>
            <h2 style={{ color: '#fff', fontSize: 30, lineHeight: '36px', margin: '0 0 14px', letterSpacing: '-0.02em' }}>
              Hand money movement to an agent
            </h2>
            <p style={{ color: '#9aa3b2', fontSize: 16, lineHeight: '24px', margin: '0 0 24px', maxWidth: 460 }}>
              Drop in our MCP server and Claude, Cursor, or your own agent can onboard businesses, pick how money moves, and create payments
              through the same typed, idempotent API your developers use — with guardrails you control.
            </p>
            <Button size="large" variant="invert" rightIcon="arrow-right" onClick={() => go('/agents')}>
              Explore AI &amp; agents
            </Button>
          </div>
          <div style={{ position: 'relative' }}>
            <CodeBlock
              dark
              title="claude_desktop_config.json"
              maxHeight={220}
              code={`{
  "mcpServers": {
    "melio": {
      "command": "npx",
      "args": ["-y", "@melio/mcp"],
      "env": { "MELIO_API_KEY": "melio_secret_staging01_…" }
    }
  }
}`}
            />
          </div>
        </div>
      </section>

      {/* ── What's in the box ── */}
      <section style={{ marginBottom: 56 }}>
        <SectionHead eyebrow="What's in the box" title="Everything a payout needs, nothing it doesn't" />
        <div className="grid-3">
          <FeatureBox icon="business" title="Businesses &amp; KYC">
            Onboard one business or thousands. Eligibility tells you exactly which fields are still missing.
          </FeatureBox>
          <FeatureBox icon="bank" title="Ways to pay in">
            Connect bank accounts and cards. We only ever return the last four digits — full numbers never leave the vault.
          </FeatureBox>
          <FeatureBox icon="paper-check" title="Every way to pay out">
            Bank transfer, wire, virtual card, or international — or have Melio print and mail a check on your behalf. No printer required.
          </FeatureBox>
          <FeatureBox icon="clock" title="Observable lifecycle">
            Every payment renders a status tracker: scheduled → in progress → sent → completed.
          </FeatureBox>
          <FeatureBox icon="document" title="Test mode">
            A pre-seeded sandbox with sample data and deterministic simulators for returns and KYC outcomes.
          </FeatureBox>
          <FeatureBox icon="notification" title="Webhooks &amp; events">
            Subscribe to payment status changes. Inspect payloads, your response, and re-trigger any event.
          </FeatureBox>
        </div>
      </section>
    </div>
  );
}

export { DevHome, SectionHead };
